import React, { useState, useEffect, useRef } from 'react';
import './About.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import apiService from '../services/api';

// --- Reusable Components ---

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.15 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? 'reveal-visible' : ''} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}
      {...rest}
    >
      {children}
    </Tag>
  );
}

function CountStat({ value, label }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref]);

  const match = value.match(/^(\d+)(.*)$/);
  const target = match ? parseInt(match[1], 10) : 0;
  const suffix = match ? match[2] : '';
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!visible) return;
    const reduceMotion = typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) { setDisplay(target); return; }

    let raf;
    const duration = 1200;
    let start = null;
    const step = (ts) => {
      if (start === null) start = ts;
      const progress = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.floor(eased * target));
      if (progress < 1) raf = requestAnimationFrame(step);
      else setDisplay(target);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [visible, target]);

  return (
    <div ref={ref} className="stat-item">
      <strong>{display}{suffix}</strong>
      <span>{label}</span>
    </div>
  );
}

// --- Main About Component ---

export default function About() {
  const [aboutData, setAboutData] = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [stats, setStats] = useState([]);
  const [differentiators, setDifferentiators] = useState([]);
  const [partners, setPartners] = useState([]);
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all about data from API
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        
        // Fetch all endpoints in parallel
        const [
          aboutResult,
          storyResult,
          statsResult,
          diffResult,
          partnersResult,
          volunteersResult
        ] = await Promise.all([
          apiService.fetchAboutSections(),
          apiService.fetchAboutStory(),
          apiService.fetchAboutStats(),
          apiService.fetchAboutDifferentiators(),
          apiService.fetchAboutPartners(),
          apiService.fetchVolunteers()
        ]);
        
        console.log('About Result:', aboutResult);
        console.log('Story Result:', storyResult);
        console.log('Stats Result:', statsResult);
        console.log('Diff Result:', diffResult);
        console.log('Partners Result:', partnersResult);
        console.log('Volunteers Result:', volunteersResult);
        
        // Process about data
        if (aboutResult?.status === 'success' && aboutResult?.data?.length > 0) {
          setAboutData(aboutResult.data[0]);
          console.log('✅ About data set successfully');
        } else {
          console.warn('⚠️ No about data found');
        }
        
        // Process milestones
        if (storyResult?.status === 'success' && storyResult?.data?.length > 0) {
          const sorted = storyResult.data.sort((a, b) => 
            parseInt(a.display_order || 0) - parseInt(b.display_order || 0)
          );
          setMilestones(sorted);
          console.log('✅ Milestones set:', sorted.length);
        } else {
          console.warn('⚠️ No milestones found');
        }
        
        // Process stats
        if (statsResult?.status === 'success' && statsResult?.data?.length > 0) {
          setStats(statsResult.data);
          console.log('✅ Stats set:', statsResult.data.length);
        } else {
          console.warn('⚠️ No stats found');
        }
        
        // Process differentiators
        if (diffResult?.status === 'success' && diffResult?.data?.length > 0) {
          setDifferentiators(diffResult.data);
          console.log('✅ Differentiators set:', diffResult.data.length);
        } else {
          console.warn('⚠️ No differentiators found');
        }
        
        // Process partners
        if (partnersResult?.status === 'success' && partnersResult?.data?.length > 0) {
          setPartners(partnersResult.data);
          console.log('✅ Partners set:', partnersResult.data.length);
        } else {
          console.warn('⚠️ No partners found');
        }
        
        // Process volunteers - filter only active volunteers
        if (volunteersResult?.status === 'success' && volunteersResult?.data?.length > 0) {
          const activeVolunteers = volunteersResult.data.filter(v => v.status === 'active');
          setVolunteers(activeVolunteers);
          console.log('✅ Volunteers set:', activeVolunteers.length);
        } else {
          console.warn('⚠️ No volunteers found');
        }
        
      } catch (err) {
        console.error('❌ Error fetching data:', err);
        setError(err.message || 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, []);

  // Helper function to render HTML content safely
  const createMarkup = (htmlContent) => {
    return { __html: htmlContent };
  };

  // Helper function to parse image from JSON
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    try {
      const parsed = typeof imageData === 'string' ? JSON.parse(imageData) : imageData;
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0];
      }
      return parsed;
    } catch (e) {
      return imageData;
    }
  };

  // Clean HTML from Summernote
  const cleanHtml = (html) => {
    if (!html) return '';
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || '';
  };

  // Fallback data if API fails
  const fallbackStats = [
    { value: '5000+', label: 'Lives Transformed' },
    { value: '670+', label: 'Free Boarding Students' },
    { value: '36', label: 'Years of Service' },
    { value: '100%', label: 'Free of Cost' },
  ];

  const fallbackDifferentiators = [
    { icon: 'fa-graduation-cap', title: 'We Educate', desc: 'Comprehensive education from Nursery to Junior College, tailored for visually challenged students.' },
    { icon: 'fa-home', title: 'We House', desc: 'Free, safe, and nurturing residential facilities with nutritious meals, four times a day.' },
    { icon: 'fa-laptop', title: 'We Empower', desc: 'Pioneering computer education, sports, and vocational training to build skills for life.' },
    { icon: 'fa-heart', title: 'We Care', desc: 'Regular medical checkups, eye camps, and unwavering support for every student\'s well-being.' },
  ];

  const fallbackMilestones = [
    { year: '1991', title: 'Founded with 4 Students', desc: 'Dr. Goud starts Devnar School for the Blind with a vision to empower visually challenged children.' },
    { year: '2002', title: 'Best Institution Award', desc: 'President of India Dr. A.P.J. Abdul Kalam awards Devnar as the Best Institution for the Blind.' },
    { year: '2004', title: 'Dr. B.C. Roy National Award', desc: 'Dr. Saibaba Goud receives the prestigious Dr. B.C. Roy National Award for his social medical relief work.' },
    { year: '2008', title: 'National Recognition', desc: 'President Pratibha Patil awards Devnar again as the Best Institution for the Blind.' },
    { year: '2017', title: 'National Award from President Kovind', desc: 'President Ram Nath Kovind honors Devnar for its continued excellence.' },
    { year: '2019', title: 'Best Braille Printing Press', desc: 'Vice President M. Venkaiah Naidu awards the best Braille Printing Press in the country.' },
    { year: '2024', title: 'Expanding Horizons', desc: 'Devnar continues to grow, empowering over 500 students with free education and boarding.' },
  ];

  const displayStats = stats.length > 0 ? stats : fallbackStats;
  const displayMilestones = milestones.length > 0 ? milestones : fallbackMilestones;
  const displayDifferentiators = differentiators.length > 0 ? differentiators : fallbackDifferentiators;

  return (
    <>
      <Header />
      <div className="about-page devnar">
        <a className="skip-link" href="#main-about">Skip to main content</a>

        {/* --- Page Hero --- */}
        <section className="page-hero" style={{ backgroundImage: `linear-gradient(rgba(11,42,84,0.78), rgba(11,42,84,0.78)), url('assets/img/IMG_7249-scaled-740x545.jpg')` }}>
          <div className="wrap hero-content-about">
            <Reveal>
              <h1>About Us</h1>
              <p>Three decades of building futures for visually challenged children.</p>
            </Reveal>
          </div>
        </section>

        <main id="main-about" className="wrap page-content-about">

          {/* --- Introduction & Founder --- */}
          <section className="intro-founder">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Journey</span>
              <h2>Helping Today. Helping Tomorrow.</h2>
              <div className="rule" />
            </Reveal>
            <div className="founder-grid">
              <Reveal className="founder-text" as="div" delay={80}>
                {loading ? (
                  <p>Loading about information...</p>
                ) : aboutData ? (
                  <>
                    {aboutData.description_en && (
                      <div dangerouslySetInnerHTML={createMarkup(aboutData.description_en)} />
                    )}
                    
                    {aboutData.vision_en && (
                      <div style={{ marginTop: '2rem' }}>
                        <h3 style={{ color: '#0b2a54', marginBottom: '0.5rem' }}>Our Vision</h3>
                        <p>{aboutData.vision_en}</p>
                      </div>
                    )}
                    
                    {aboutData.mission_en && (
                      <div style={{ marginTop: '1.5rem' }}>
                        <h3 style={{ color: '#0b2a54', marginBottom: '0.5rem' }}>Our Mission</h3>
                        <p>{aboutData.mission_en}</p>
                      </div>
                    )}
                  </>
                ) : error ? (
                  <p>Unable to load about information. Please try again later.</p>
                ) : (
                  <p>No about information available.</p>
                )}
                
                <div className="awards-highlight">
                  <span><i className="fas fa-trophy"></i> National Award Winner</span>
                  <span><i className="fas fa-medal"></i> Best Institution for the Blind</span>
                </div>
              </Reveal>
              <Reveal className="founder-card" as="div" delay={150}>
                <img 
                  src={aboutData?.image || 'assets/img/uploads/founder-saibaba-goud.jpg'} 
                  alt="Dr. A. Saibaba Goud, Founder of Devnar Foundation" 
                  loading="lazy" 
                />
                <div className="founder-info">
                  <h3>Dr. A. Saibaba Goud</h3>
                  <p>Founder &amp; Padma Shri Awardee</p>
                </div>
                
              </Reveal>
            </div>
          </section>

          {/* --- Stats - Dynamic from API --- */}
          <section className="stats-wrap-about dot-field">
            <div className="stats-about">
              {displayStats.map((stat) => (
                <CountStat 
                  key={stat.id || stat.label} 
                  value={stat.value} 
                  label={stat.label} 
                />
              ))}
            </div>
          </section>

          {/* --- Milestones / Timeline - Dynamic from API --- */}
          <section className="milestones">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our History</span>
              <h2>Key Milestones</h2>
              <div className="rule" />
            </Reveal>
            <div className="timeline">
              {displayMilestones.map((item, index) => (
                <Reveal key={item.id || item.year || index} className="timeline-item" delay={index * 60}>
                  <div className="timeline-year">{item.year}</div>
                  <div className="timeline-content">
                    <h3>{item.title_en || item.title}</h3>
                    <p>{item.description_en || item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Differentiators - Dynamic from API --- */}
          <section className="differentiators">
            <Reveal className="section-title" as="div">
              <span className="eyebrow">Our Core</span>
              <h2>What Makes Us Different</h2>
              <div className="rule" />
            </Reveal>
            <div className="diff-grid">
              {displayDifferentiators.map((item, index) => (
                <Reveal key={item.id || item.title} className="diff-card" delay={index * 70}>
                  <div className="diff-icon">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h3>{item.title}</h3>
                  <p>{item.desc || item.description_en}</p>
                </Reveal>
              ))}
            </div>
          </section>

          {/* --- Volunteer Team - Dynamic from Database --- */}
          {volunteers.length > 0 && (
            <section className="volunteer-team">
              <Reveal className="section-title" as="div">
                <span className="eyebrow">Our Supporters</span>
                <h2>Meet Our Volunteer Team</h2>
                <div className="rule" />
              </Reveal>
              <div className="volunteer-grid">
                {volunteers.map((vol, index) => {
                  const imageUrl = getImageUrl(vol.image);
                  return (
                    <Reveal key={vol.id} className="volunteer-card" delay={index * 60}>
                      {imageUrl ? (
                        <img src={imageUrl} alt={vol.full_name_en || vol.full_name} loading="lazy" />
                      ) : (
                        <div className="volunteer-avatar-placeholder">
                          <i className="fas fa-user"></i>
                        </div>
                      )}
                      <h4>{vol.full_name_en || vol.full_name}</h4>
                      {vol.role && <span className="volunteer-role">{vol.role}</span>}
                      {vol.joined_date && (
                        <span className="volunteer-joined">
                          <i className="fas fa-calendar-alt"></i> Joined {new Date(vol.joined_date).toLocaleDateString()}
                        </span>
                      )}
                    </Reveal>
                  );
                })}
              </div>
            </section>
          )}

          {/* --- CTA Section --- */}
          <Reveal as="section" className="cta-band-about">
            <h2>Be a part of this incredible journey.</h2>
            <p>Support our mission to empower visually challenged children with education and dignity.</p>
            <div className="cta-buttons">
              <a className="btn btn-primary" href="/donate">
                <i className="fas fa-heart"></i> Donate Now
              </a>
              <a className="btn btn-outline btn-outline-light" href="/volunteer">
                <i className="fas fa-hands-helping"></i> Become a Volunteer
              </a>
            </div>
          </Reveal>

        </main>
      </div>
      <Footer />
    </>
  );
}